import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { cancelRelayFax } from 'store/thunks/workQueueThunks';

import {
  FAX_MAY_BE_AT_PHARMACY_CODES,
  relayFaxDestination,
} from '../constants/nonRoutableRequests';
import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableCancelReasonField from './NonRoutableCancelReasonField';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';

const faxMayHaveBeenSent = (_detail, result) => result?.faxMayHaveBeenSent === true;

/**
 * Whether the pharmacy may ALREADY hold a fax for this order, judged from the row before the click:
 * the fax service accepted one and later said it failed, or its outcome is unknown (the same codes
 * that make Reconcile meaningful, RECONCILABLE_FAX_CODES in core), or a resend's claim went stale
 * and its outcome never came back. Only when none of these holds can the dialog promise that the
 * prescription will not be sent or filled.
 */
const faxMayBeAtPharmacy = (item) =>
  FAX_MAY_BE_AT_PHARMACY_CODES.has(item.relayErrorCode) || item.isStaleClaim === true;

/**
 * M4 — the cancel went through and only its read-back failed, so the result never arrived. The
 * server puts `faxMayHaveBeenSent` in the error's extensions; ABSENT IS TREATED AS TRUE, because a
 * needless phone call costs nothing beside a prescription filled twice.
 */
const readBackMayHaveSent = (described) =>
  described?.code === 'RELAY_FAX_ACTION_READ_BACK_FAILED' && described.faxMayHaveBeenSent !== false;

const successMessageFor = (atRisk) => (detail, result) => {
  if (faxMayHaveBeenSent(detail, result)) {
    return {
      variant: 'warning',
      message:
        'Fax order cancelled — but the fax may already be at the pharmacy. Phone them and tell ' +
        'them not to fill it.',
    };
  }
  return atRisk
    ? {
        variant: 'warning',
        message:
          'Fax order cancelled. Nothing more will be sent, but the pharmacy may already have the ' +
          'earlier fax — phone them and tell them not to fill it.',
      }
    : 'Fax order cancelled. Nothing more will be sent and this prescription will not be filled.';
};

/**
 * CANCEL A PARKED RELAY FAX (POM-6279). The relay order AND the request are cancelled together.
 * POM-6545: the partner is then sent the ORDER_CANCELED webhook carrying the reason chosen here, so
 * a reason is required before Cancel is enabled.
 *
 * A FAX CANNOT BE RECALLED. When the send was in flight at the moment of cancelling, the server says
 * so (`faxMayHaveBeenSent`), and the dialog then STAYS OPEN with the instruction to phone the
 * pharmacy — a snackbar that fades is not where "the pharmacy may fill this anyway" belongs. When
 * the row already says the pharmacy may hold a fax, the instruction comes BEFORE the click instead
 * of a promise the dialog cannot keep.
 */
const CancelRelayFaxDialog = ({ open, onClose, item }) => {
  const atRisk = faxMayBeAtPharmacy(item);
  const successMessage = useMemo(() => successMessageFor(atRisk), [atRisk]);
  const pharmacy = relayFaxDestination(item);
  const { submit, submitting, outcome, completed, dismiss } = useNonRoutableResolution({
    item,
    action: cancelRelayFax,
    successMessage,
    onClose,
    open,
    holdOpen: faxMayHaveBeenSent,
  });

  const [cancelReason, setCancelReason] = useState('');

  // The row menu keeps the dialog mounted, so a previous pick must not carry over to the next open.
  useEffect(() => {
    if (open) setCancelReason('');
  }, [open]);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  const canSubmit = !completed && (!outcome || nonRoutableRetryIsSafe(outcome.described));
  const mayHaveBeenSent = !!completed || readBackMayHaveSent(outcome?.described);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='CancelRelayFaxDialog-title'
    >
      <DialogTitle id='CancelRelayFaxDialog-title'>Cancel this fax order?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        {mayHaveBeenSent && (
          <Alert severity='warning' data-testid='relay-fax-may-have-been-sent'>
            <AlertTitle>The fax may already be at the pharmacy</AlertTitle>
            {`The order is cancelled, but the fax may already have reached the pharmacy. Phone ` +
              `${pharmacy} and tell them not to fill this prescription.`}
          </Alert>
        )}

        {!mayHaveBeenSent && atRisk && (
          <Alert severity='warning' data-testid='relay-fax-cancel-may-be-at-pharmacy'>
            <AlertTitle>A fax cannot be recalled</AlertTitle>
            {`The pharmacy may already have this fax. Cancelling stops anything more being sent; ` +
              `phone ${pharmacy} and tell them not to fill it.`}
          </Alert>
        )}

        {!mayHaveBeenSent && (
          <Alert severity='error' sx={{ mt: atRisk ? 2 : 0 }}>
            <AlertTitle>
              {atRisk
                ? 'The fax order and this request will be cancelled'
                : 'This prescription will not be sent or filled'}
            </AlertTitle>
            The fax order and this request are both cancelled.
            <strong> The partner is sent a cancellation notice with the reason you choose. </strong>
            This cannot be undone.
          </Alert>
        )}

        {canSubmit && (
          <NonRoutableCancelReasonField
            id='CancelRelayFaxDialog-cancelReason-dropdown'
            value={cancelReason}
            onChange={setCancelReason}
            disabled={submitting}
          />
        )}

        {outcome && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {mayHaveBeenSent || (outcome && !canSubmit) ? 'Close' : 'Keep the order'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='error'
            loading={submitting}
            disabled={!cancelReason}
            onClick={() => submit({ cancelReason })}
            id={`CancelRelayFaxDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Cancel Fax Order
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

CancelRelayFaxDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    destinationName: PropTypes.string,
    relayErrorCode: PropTypes.string,
    isStaleClaim: PropTypes.bool,
  }).isRequired,
};

export default CancelRelayFaxDialog;
